import { forwardRef } from "react";
import { Check, ChevronDown, RotateCcw } from "lucide-react";
import { badgeVariants } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Hint } from "@/components/ui/tooltip";
import { quickActionFor, type TargetColumn } from "@/lib/column-targets";
import { kindColor } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * A card's state as a menu of the board's columns, so moving it never needs the
 * edit mode. `children` is the trigger (the header's badge, the details' field);
 * picking the current state does nothing.
 */
export function StateMenu({
  column,
  columns,
  onPick,
  children,
}: {
  column: TargetColumn;
  columns: readonly TargetColumn[];
  onPick: (columnId: string) => void;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[12rem]">
        <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Move to</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={column.id} onValueChange={(value) => value !== column.id && onPick(value)}>
          {[...columns]
            .sort((a, b) => a.position - b.position)
            .map((entry) => (
              <DropdownMenuRadioItem key={entry.id} value={entry.id}>
                <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: kindColor(entry.kind) }} />
                {entry.name}
              </DropdownMenuRadioItem>
            ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The header badge as a `StateMenu` trigger: the badge's look, plus a chevron that says it opens. */
export const StateBadgeTrigger = forwardRef<HTMLButtonElement, { column: TargetColumn } & React.ComponentPropsWithoutRef<"button">>(
  ({ column, className, ...props }, ref) => {
    const tint = kindColor(column.kind);
    return (
      <button
        ref={ref}
        type="button"
        aria-label={`State: ${column.name}. Change state`}
        {...props}
        className={cn(
          badgeVariants({ variant: "tinted" }),
          "cursor-pointer outline-none hover:brightness-110 focus-visible:ring-2 focus-visible:ring-ring/60",
          className,
        )}
        style={{ color: tint, backgroundColor: `color-mix(in oklab, ${tint} 16%, transparent)` }}
      >
        {column.name}
        <ChevronDown className="opacity-70" />
      </button>
    );
  },
);
StateBadgeTrigger.displayName = "StateBadgeTrigger";

/**
 * The sheet bar's one-tap action: "Mark done" on a card in a review-kind column,
 * "Reopen" on a done one, nothing anywhere else. Icon-only below `sm`, with a Hint.
 */
export function QuickStateButton({
  column,
  columns,
  busy = false,
  onMove,
}: {
  column: TargetColumn;
  columns: readonly TargetColumn[];
  busy?: boolean;
  onMove: (target: TargetColumn, action: "done" | "reopen") => void;
}) {
  const quick = quickActionFor(column, columns);
  if (!quick) return null;
  const done = quick.action === "done";
  const label = done ? "Mark done" : "Reopen";
  const Icon = done ? Check : RotateCcw;
  return (
    <Hint label={done ? `Move to ${quick.target.name}` : `Reopen in ${quick.target.name}`} side="bottom">
      <button
        type="button"
        disabled={busy}
        onClick={() => onMove(quick.target, quick.action)}
        aria-label={label}
        data-quick-action={quick.action}
        className={cn(
          "mr-1 inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[12.5px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/60 disabled:opacity-60 sm:px-2.5",
          done ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-muted text-foreground hover:bg-muted/70",
        )}
      >
        <Icon className="size-3.5" />
        <span className="hidden sm:inline">{label}</span>
      </button>
    </Hint>
  );
}
